import React from 'react';
import cn from 'bem-cn';
import { PromptCardRedesign } from '@ai-platform/utils';

import { RoutePaths } from '../../constants';
import { useExperimentFlag } from '../../contexts';
import { pluralize } from '../../utils/pluralize/pluralize';
import { getDaysUntil } from '../../utils/date/date';
import { ScreenSize, useGetScreenSize, useVideoOnHover } from '../../hooks';
import { useParallax } from '../PromptCard/useParallax';
import { Link, Video } from '../UI';

import { ChallengeCardProps } from './ChallengeCard.types';

import './ChallengeCard.scss';

const b = cn('challenge');

export const ChallengeCard: React.FC<ChallengeCardProps> = ({
    challenge,
    className,
    withHover = true,
    alwaysOn = false,
}) => {
    const { id, title, videoData, deadline } = challenge;
    const { url: videoUrl, poster: videoPoster } = videoData;

    const screenSize = useGetScreenSize();
    const { onMouseEnter, onMouseLeave, videoRef } = useVideoOnHover();
    const { panelRef, transformX, transformY } = useParallax(1);
    const experimentFlag = useExperimentFlag('promptCardRedesign');

    const isMobile = screenSize === ScreenSize.Mobile;
    const isNewDesignCard = isMobile && experimentFlag === PromptCardRedesign.New;
    const daysLeft = getDaysUntil(deadline);

    // Включаем hover эффекты только на десктопе
    const needHover = !isMobile && screenSize !== ScreenSize.Tablet && withHover;

    // Новая структура слоев параллакса - только параллакс трансформации
    const cardParallaxStyle = needHover
        ? {
            transform: `perspective(200px) rotateY(${transformX}deg) rotateX(${transformY}deg)`,
        }
        : {};

    const backgroundStyle = {
        '--bg-image': `url(${videoPoster})`,
    } as React.CSSProperties;

    return (
        <Link
            to={`${RoutePaths.Challenge}/${id}`}
            className={b({ 'with-hover': withHover, 'always-on': alwaysOn, experiment: isNewDesignCard }, [className])}
            ref={panelRef}
            onMouseEnter={videoUrl && !alwaysOn ? onMouseEnter : undefined}
            onMouseLeave={videoUrl && !alwaysOn ? onMouseLeave : undefined}
        >
            {needHover && <div className={b('hover-trigger')} />}
            <div style={cardParallaxStyle} className={b('parallax')}>
                <div className={b('background')} style={backgroundStyle}>
                    {videoUrl && (
                        <Video
                            ref={videoRef}
                            src={videoUrl}
                            aria-label={title}
                            className={b('video')}
                            muted
                            loop
                            playsInline
                            autoPlay={alwaysOn}
                        />
                    )}
                </div>
                <div className={b('content')}>
                    <div className={b('header')}>
                        <div>Челлендж</div>
                    </div>
                    <h2 className={b('title')}>{title}</h2>
                    <div className={b('duration')}>
                        {daysLeft >= 0 ? (
                            <>
                                Осталось:
                                <br />
                                {pluralize(daysLeft + 1, { one: 'день', few: 'дня', many: 'дней' })}
                            </>
                        ) : (
                            <>
                                Приём заявок <br /> завершён
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Link>
    );
};
